<template>
  <CustomContainer :showAddButton="false">
    <div class="flex flex-col gap-4 p-2">
      <div class="flex items-center justify-between border_bottom pb-4">
        <div class="flex items-center gap-2">
          <ThemeImage
            :icon="config.icon"
            :alt="`${config.type}_icon`"
            class="size-5"
          />
          <h3 class="text-md font-semibold">
            {{ isEdit ? "Edit" : "" }} {{ config.label }}
          </h3>
        </div>
        <ThemeImage
          v-if="isEdit"
          :icon="imageNames.DELETE_GREY"
          alt="delete_icon"
          class="size-4 cursor-pointer"
          @click="handleDelete"
        />
      </div>

      <div class="form_col gap-2">
        <label for="knowledge_base_name" class="secondary_color text-[13px]">
          {{ config.fields.name.label }} *
        </label>
        <a-input
          id="knowledge_base_name"
          v-model:value="formData.name"
          :placeholder="config.fields.name.placeholder"
          class="input_medium"
        />
      </div>

      <div v-if="config.type === 'text'" class="form_col gap-2">
        <label for="knowledge_base_text" class="secondary_color text-[13px]">
          {{ config.fields.text.label }}
        </label>
        <a-textarea
          id="knowledge_base_text"
          v-model:value="formData.text"
          :placeholder="config.fields.text.placeholder"
          :rows="8"
          class="textarea_large"
        />
      </div>

      <div v-if="config.type === 'link'" class="form_col gap-2">
        <label for="knowledge_base_url" class="secondary_color text-[13px]">
          {{ config.fields.url.label }} *
        </label>
        <a-input
          id="knowledge_base_url"
          v-model:value="formData.url"
          :placeholder="config.fields.url.placeholder"
          class="input_medium"
          :class="{ input_error: urlError }"
          @blur="handleUrlValidation"
          @change="urlError = ''"
        />
        <span
          v-if="urlError"
          class="error_msg text-xs proxima_nova font-normal"
        >
          {{ urlError }}
        </span>
      </div>

      <div v-if="config.type === 'file'" class="form_col gap-2">
        <label for="knowledge_base_file" class="secondary_color text-[13px]">
          {{ config.fields.file.label }}
        </label>
        
        <div v-if="fileObject" class="w-fit">
          <div
            v-if="formData.file?.url"
            class="block cursor-pointer"
            @click="handleFileLinkClick"
          >
            <AttachedItem
              :file="fileObject"
              :downloadable="false"
              @remove="handleFileRemove"
            />
          </div>

          <AttachedItem
            v-else
            :file="fileObject"
            :downloadable="false"
            @remove="handleFileRemove"
          />
        </div>

        <UploadComponent
          v-else
          source="knowledge_base_file"
          type="document"
          height="96px"
          description=".pdf file only"
          v-model="uploadedFile"
        />
      </div>

      <div class="flex justify-start">
        <a-button
          class="primary_medium"
          @click="handleSave"
          :disabled="!isFormValid || props.isSaving"
          :loading="props.isSaving"
        >
          {{ props.isSaving ? 'Saving...' : 'Save' }}
        </a-button>
      </div>
    </div>
  </CustomContainer>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import { message } from "ant-design-vue";
import ThemeImage from "@/src/components/ThemeImage.vue";
import CustomContainer from "@/src/components/CustomContainer.vue";
import UploadComponent from "@/src/components/upload_components/UploadComponent.vue";
import AttachedItem from "@/src/components/attachment_components/AttachedItem.vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { normalizeUrl, validateUrl } from "@/src/utils/voiceAgentUtils.js";

const props = defineProps({
  config: {
    type: Object,
    required: true,
  },
  itemData: {
    type: Object,
    default: null,
  },
  isEdit: {
    type: Boolean,
    default: false,
  },
  agentId: {
    type: Number,
    default: null,
  },
  knowledgeBaseId: {
    type: [Number, String],
    default: null,
  },
  isSaving: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["save", "delete"]);

const uploadedFile = ref(null);
const urlError = ref("");

const formData = ref({
  name: "",
  text: "",
  url: "",
  file: null,
  type: props.config.type,
});

const isFormValid = computed(() => {
  if (!formData.value.name?.trim()) {
    return false;
  }

  if (props.config.type === "text") {
    return formData.value.text?.trim();
  }

  if (props.config.type === "link") {
    const urlValidation = validateUrl(formData.value.url);
    return formData.value.url?.trim() && urlValidation.isValid;
  }

  if (props.config.type === "file") {
    return formData.value.file;
  }

  return false;
});

const fileObject = computed(() => {
  const file = formData.value.file;
  return file
    ? {
        id: "knowledge_base_file",
        file_name: file.name,
        file_size: file.size,
      }
    : null;
});

const handleFileRemove = () => {
  formData.value.file = null;
};

const handleUrlValidation = () => {
  const { isValid, error } = validateUrl(formData.value.url);
  urlError.value = error;
  return isValid;
};

const handleFileLinkClick = (event) => {
  try {
    if (!event || !formData.value.file?.url) {
      return;
    }

    const target = event.target;
    const isRemoveButton = target.closest('button') || target.closest('svg');
    
    if (isRemoveButton) {
      return;
    }

    window.open(formData.value.file.url, '_blank', 'noopener,noreferrer');
  } catch (error) {
    console.error("Error opening file:", error);
  }
};

const handleSave = () => {
  if (!isFormValid.value || props.isSaving) {
    return;
  }

  if (props.config.type === 'link') {
    const isValid = handleUrlValidation();
    if (!isValid) {
      return;
    }
  }

  try {
    const itemData = {
      ...formData.value,
      type: props.config.type,
    };

    if (props.isEdit && props.itemData?.id) {
      itemData.id = props.itemData.id;
    }

    
    if (itemData.type === 'link' && itemData.url) {
      itemData.url = normalizeUrl(itemData.url);
    }

    emit("save", itemData);
  } catch (error) {
    console.error("Error saving knowledge base item:", error);
    message.error("Failed to save knowledge base item");
  }
};

const handleDelete = () => {
  try {
    emit("delete");
  } catch (error) {
    console.error("Error deleting knowledge base item:", error);
  }
};

const initializeFormData = (newData) => {
  if (!newData) {
    formData.value = {
      name: "",
      text: "",
      url: "",
      file: null,
      type: props.config.type,
    };
    return;
  }

  formData.value = {
    name: newData.name || "",
    text: newData.text || "",
    url: newData.url || "",
    file: newData.file || null,
    type: newData.type || props.config.type,
  };
};

watch(() => props.itemData, initializeFormData, { immediate: true });

watch(uploadedFile, (newFile) => {
  if (newFile) {
    formData.value.file = newFile;
  }
});
</script>

